import { Constants } from "appConstants";
import ApplicationNewRecordOptions, { ApplicationNewRecordOption } from "appConstants/ApplicationNewRecordOptions";
import { Button, Input, Label, MultiFileDisplay, SelectForm, TextArea } from "components";

import { IcoUpload } from "assets/icons";
import colors from "colors";
import Datepicker from "components/Datepicker";
import FileInput from "components/FileInput";
import MultiSelect from "components/SelectForm/MultiSelect";
import TagRadioInput from "components/TagRadioInput";
import ToastWithTitle from "components/ToastWithTitle";
import { baseConfig } from "config";
import { addDays } from "date-fns";
import { useFormik } from "formik";
import { ErrorHelper } from "helpers";
import OptionHelper, { OptionLike } from "helpers/OptionHelper";
import useFileUploadQueue from "hooks/useFileUploadQueue";
import useUserToken from "hooks/useUserToken";
import { ExemptionMotive } from "models/ExemptionMotive";
import { ExemptionMotiveType } from "models/ExemptionMotiveType";
import { PlateConsult } from "models/PlateConsult";
import { TagCompany } from "models/TagCompany";
import { TollStation } from "models/TollStation";
import { ApplicationEventType } from "models/types";
import { VehicleCategory } from "models/VehicleCategory";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApplicationService } from "services/applicationService";
import { ExemptionMotiveService } from "services/exemptionMotive";
import { ExemptionMotiveTypeService } from "services/exemptionMotiveType";
import { PlateConsultResponse, PlateConsultService } from "services/plateTagService";
import { TagCompanyService } from "services/tagCompanyService";
import { TollStationService } from "services/tollStationService";
import { DocumentLike, RegisterEventRequest } from "services/types";
import { VehicleCategoryService } from "services/vehicleCategoryService";
import { RootState } from "store";
import { initialStateNewRecord, reset, setValues } from "store/features/newRecord/slice";
import * as Yup from "yup";
import { CancelNewRecordModal } from "../../components";
import { TabComponentProps } from "../../types";
import "./styles.scss";

export type NewRecordForm = {
	action?: ApplicationNewRecordOption | null;
	comment: string;
	commentDocuments: DocumentLike[];
	response?: string;
	responseDocuments: DocumentLike[];
	exemptionMotive?: ExemptionMotive;
	exemptionMotiveType?: ExemptionMotiveType;
	propertyCode?: string;
	exemptionInitDatetime?: Date;
	exemptionEndDatetime?: Date;
	tollStations?: TollStation[];
	vehicleCategory?: VehicleCategory;
	tollBoothType?: string;
	tagNumber?: string;
	tagCompany?: TagCompany;
};

export default function ApplicationPageNewRecord({ application, reloadApplication }: TabComponentProps) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const stateValues = useSelector((state: RootState) => state.newRecord);
	const exemptionMotiveService = useMemo(ExemptionMotiveService.getInstance, []);
	const exemptionMotiveTypeService = useMemo(ExemptionMotiveTypeService.getInstance, []);
	const tollStationService = useMemo(TollStationService.getInstance, []);
	const vehicleCategoryService = useMemo(VehicleCategoryService.getInstance, []);
	const [exemptionMotivesOptions, setExemptionMotivesOptions] = useState<OptionLike[]>([]);
	const [exemptionMotiveTypesOptions, setExemptionMotiveTypesOptions] = useState<OptionLike[]>([]);
	const [tollStationsOptions, setTollStationsOptions] = useState<OptionLike[]>([]);
	const [vehicleCategoriesOptions, setVehicleCategoriesOptions] = useState<OptionLike[]>([]);
	const [isOpenModalCancel, setIsOpenModalCancel] = useState(false);
	const [plateData, setPlateData] = useState<PlateConsult[]>([]);
	const [submittedTagNumber, setSubmittedTagNumber] = useState("");
	const tagCompanyService = useMemo(TagCompanyService.getInstance, []);
	const [isLoadingSave, setIsLoadingSave] = useState(false);
	const applicationService = useMemo(ApplicationService.getInstance, []);
	const internalUser = useUserToken();
	const [tagCompanies, setTagCompanies] = useState<TagCompany[] | null>(null);
	const responseActions = [ApplicationEventType.SOLICITAR_ALTERACAO, ApplicationEventType.SOLICITACAO_REPROVADA];
	const isResponseAction = (action: ApplicationEventType) => {
		return responseActions.includes(action);
	};
	const validationSchema = Yup.object().shape({
		action: Yup.object().shape({
			value: Yup.string().required()
		}),
		comment: Yup.string().required(),
		response: Yup.string().when("action", {
			is: (action: any) => isResponseAction(action?.value),
			then: Yup.string()
		}),
		exemptionMotive: Yup.object().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.object().required()
		}),
		exemptionMotiveType: Yup.object().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.object().required(),
			otherwise: Yup.object().nullable()
		}),
		propertyCode: Yup.string(),
		exemptionInitDatetime: Yup.date().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.date().required()
		}),
		exemptionEndDatetime: Yup.date().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.date()
				.required()
				.test(
					"endAterInit",
					"A data final da isenção deve ser posterior à data inicial",
					(val, context) => !!(val && val > context.parent.exemptionInitDatetime)
				)
		}),
		tollStations: Yup.array().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.array().required()
		}),
		vehicleCategory: Yup.object().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.object().required()
		}),
		tollBoothType: Yup.string().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.string().required()
		}),
		tagNumber: Yup.string().when("action", {
			is: (action: any) => action?.value === ApplicationEventType.SOLICITACAO_APROVADA,
			then: Yup.string()
		})
	});

	const getDefaultDataRequest = (values: NewRecordForm): RegisterEventRequest => {
		const eventType = values.action!.value;

		const data = {
			event: {
				type: eventType as ApplicationEventType,
				description: values.comment
			},
			documents: values.commentDocuments
		};

		return data;
	};

	useEffect(() => {
		tagCompanyService
			.listAll()
			.then((response) => {
				setTagCompanies(response);
			})
			.catch((e) => toast.error(ErrorHelper.getResponseErrorMessage(e)));
	}, []);

	const registerApproveEvent = async (values: NewRecordForm) => {
		const dataComment: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.ADICIONAR_COMENTARIO,
				description: values.comment
			},
			documents: values.commentDocuments
		};
		const dataCommentRespostaInterested: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.RESPOSTA_INTERESSADO,
				description: values.response
			},
			documents: values.responseDocuments
		};
		const dataApprove: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.SOLICITACAO_APROVADA,
				description: values.response
			},
			documents: values.responseDocuments,
			approvalData: {
				exemptionMotive: values.exemptionMotive!.id,
				exemptionMotiveType: values.exemptionMotiveType!.id,
				propertyCode: values.propertyCode,
				exemptionInitDatetime: values.exemptionInitDatetime!.toISOString(),
				exemptionEndDatetime: values.exemptionEndDatetime!.toISOString(),
				tollStations: values.tollStations!.map((t) => t.id),
				vehicleCategory: values.vehicleCategory!.id,
				tollBoothType: values.tollBoothType!,
				tagNumber: values.tagNumber! || "",
				tagCompany: values.tagCompany!
			}
		};
		const events = [dataComment, dataApprove];
		if (values.response) {
			events.unshift(dataCommentRespostaInterested);
		}
		await applicationService.registerEvent(application!.id, events);
	};

	const registerRequestChangesEvent = async (values: NewRecordForm) => {
		const dataComment: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.ADICIONAR_COMENTARIO,
				description: values.comment
			},
			documents: values.commentDocuments
		};

		const dataResponseToApplicant: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.SOLICITAR_ALTERACAO,
				description: values.response
			},
			documents: values.responseDocuments
		};
		await applicationService.registerEvent(application!.id, [dataComment, dataResponseToApplicant]);
	};

	const registerReproveEvent = async (values: NewRecordForm) => {
		const dataComment: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.ADICIONAR_COMENTARIO,
				description: values.comment
			},
			documents: values.commentDocuments
		};

		const dataResponseToApplicant: RegisterEventRequest = {
			event: {
				type: ApplicationEventType.SOLICITACAO_REPROVADA,
				description: values.response
			},
			documents: values.responseDocuments
		};
		await applicationService.registerEvent(application!.id, [dataComment, dataResponseToApplicant]);
	};

	const registerEvent = async (values: NewRecordForm) => {
		switch (values.action?.value) {
			case ApplicationEventType.SOLICITACAO_APROVADA:
				await registerApproveEvent(values);
				break;
			case ApplicationEventType.SOLICITAR_ALTERACAO:
				await registerRequestChangesEvent(values);
				break;
			case ApplicationEventType.SOLICITACAO_REPROVADA:
				await registerReproveEvent(values);
				break;
			default:
				await applicationService.registerEvent(application!.id, [getDefaultDataRequest(values)]);
				break;
		}
	};

	const formik = useFormik({
		initialValues: stateValues as NewRecordForm,
		validationSchema,
		onSubmit: async (values) => {
			setIsLoadingSave(true);
			try {
				await registerEvent(values);
				dispatch(reset());
				toast.success(<ToastWithTitle title="Registro criado com sucesso" description="Essa ação foi registrada no histórico." />);
				reloadApplication();
				navigate(`../solicitacao/${application?.id}/historico`);
			} catch (error: any) {
				if (error.response?.data?.code === Constants.InternalResponseCodes.DUPLICATED_APPLICATION_APPROVAL) {
					toast.error(
						<ToastWithTitle
							title="Já existe uma isenção vigente para o período e ao menos uma das praças selecionadas"
							description="Altere o período de vigência ou as praças para prosseguir com a solicitação"
						/>
					);
					formik.setFieldError("tollStations", " ");
					formik.setFieldError("exemptionInitDatetime", " ");
					formik.setFieldError("exemptionEndDatetime", " ");
				} else {
					toast.error(<ToastWithTitle title="Ops! Erro ao registrar" description="Essa ação não foi salva no histórico" />);
				}
			}
			setIsLoadingSave(false);
		}
	});
	const setCommentDocuments = useCallback((newDocs: DocumentLike[]) => formik.setFieldValue("commentDocuments", newDocs), [formik.setFieldValue]);
	const setResponseDocuments = useCallback((newDocs: DocumentLike[]) => formik.setFieldValue("responseDocuments", newDocs), [formik.setFieldValue]);
	const fileUploadOptions = {
		maxFileCount: baseConfig.maxSubmissionApplicationFiles,
		acceptedFormats: Constants.acceptedDocumentFormats,
		maxByteSize: Constants.MAX_DOCUMENT_SIZE
	};
	const commentDocumentsUpload = useFileUploadQueue(formik.values.commentDocuments, setCommentDocuments, fileUploadOptions);
	const responseDocumentsUpload = useFileUploadQueue(formik.values.responseDocuments, setResponseDocuments, fileUploadOptions);

	const actionOptions = useMemo(
		() => (application?.currentUserResponsibleGroup ? ApplicationNewRecordOptions[application?.currentUserResponsibleGroup] : []),
		[application]
	);

	const getExemptionMotives = () => {
		if (!application) return;

		exemptionMotiveService
			.listExemptionMotives(application!.concessionaire?.id)
			.then((motives) => setExemptionMotivesOptions(motives.map((motive) => motive.toOption())))
			.catch(() => toast.error("Erro ao buscar motivos de isenção"));
	};

	const getExemptionMotiveTypes = () => {
		if (!formik.values.exemptionMotive) {
			toast.warning("Selecione um motivo de isenção");
			return;
		}

		exemptionMotiveTypeService
			.listExemptionMotiveTypes(formik.values.exemptionMotive.id)
			.then((types) => setExemptionMotiveTypesOptions(types.map((motiveType) => motiveType.toOption())))
			.catch(() => toast.error("Erro ao buscar tipos de isenção"));
	};

	const getTollStations = () => {
		if (!application) return;

		tollStationService
			.listByConcessionaire(application.concessionaire?.id)
			.then((tollStations) => setTollStationsOptions(tollStations.map((tollStation) => tollStation.toOption())))
			.catch(() => toast.error("Erro ao buscar praças de pedágio"));
	};

	const getVehicleCategories = () => {
		vehicleCategoryService
			.listAll()
			.then((categories) => setVehicleCategoriesOptions(categories.map((category) => category.toOption())))
			.catch(() => toast.error("Erro ao buscar categorias de veículos"));
	};

	useEffect(() => {
		dispatch(
			setValues({
				...formik.values,
				applicationId: application?.id
			})
		);
	}, [formik.values]);

	useEffect(() => {
		const consultVehiclePlate = async () => {
			const plate = application?.applicationVehicle?.plate;
			const submittedTag = application?.applicationVehicle?.tagNumber;

			if (!plate) {
				setPlateData([]);
				return;
			}

			if (submittedTag) {
				setSubmittedTagNumber(submittedTag);
			}

			try {
				const plateConsultService = PlateConsultService.getInstance();
				const result = await plateConsultService.consultPlate(plate);

				const cleanData: PlateConsult[] = result.map((item: PlateConsult) => {
					if (item.props) return item.props;
					return item;
				});

				const filteredPlateConsult: PlateConsult[] = cleanData; // Permite todas as situações filtradas pela API (incluindo isento)

				if (filteredPlateConsult) {
					setPlateData(filteredPlateConsult);
				}
			} catch (error) {
				console.error("Erro ao consultar placa:", error);
				setPlateData([]);
			}
		};

		consultVehiclePlate();
	}, [application?.applicationVehicle?.plate]);

	useEffect(() => {
		if (!plateData?.length) {
			formik.setFieldValue("tollBoothType", "MANUAL");
			return;
		}

		const activeTag = plateData.find((data) => data.tag === formik.values.tagNumber);

		const companySelected = tagCompanies?.find((el) => el.name === activeTag?.empresa);

		formik.setFieldValue("tagCompany", companySelected?.props);

		const newTollBoothType =
			activeTag && (activeTag.situacao === "ativo" || activeTag.situacao === "bloqueado" || activeTag.situacao === "isento") ? "TAG" : "MANUAL";

		if (formik.values.tollBoothType !== newTollBoothType) {
			formik.setFieldValue("tollBoothType", newTollBoothType);
		}
	}, [plateData, formik.values.tagNumber, formik.values.tollBoothType, formik.setFieldValue]);

	useEffect(() => {
		if (application && application?.currentUserResponsibleEmail !== internalUser?.email) {
			navigate(`../solicitacao/${application.id}/detalhes`);
		}

		getExemptionMotives();
		getTollStations();
		getVehicleCategories();

		if (!!application?.id && application?.id !== stateValues.applicationId) {
			formik.setValues(initialStateNewRecord);
		}
	}, [application?.id]);

	useEffect(() => {
		formik.setFieldValue("exemptionMotiveType", null);

		if (formik.values.exemptionMotive) {
			getExemptionMotiveTypes();
		}
	}, [formik.values.exemptionMotive]);

	const resetOnActionChange = () => {
		formik.setValues({
			...formik.values,
			response: "",
			responseDocuments: []
		});
	};

	const fileCountCommentDocuments =
		formik.values.commentDocuments.length + commentDocumentsUpload.notUploadedDocuments.length + commentDocumentsUpload.documentUploads.length;

	const fileCountResponseDocuments =
		formik.values.responseDocuments.length + responseDocumentsUpload.notUploadedDocuments.length + responseDocumentsUpload.documentUploads.length;

	const hasDocumentsWithError = useMemo(
		() => formik.values.commentDocuments.some((doc) => !!doc.error) || formik.values.responseDocuments.some((doc) => !!doc.error),
		[formik.values.commentDocuments, formik.values.responseDocuments]
	);

	const hasPendingDocuments = useMemo(
		() =>
			commentDocumentsUpload.notUploadedDocuments.length > 0 ||
			commentDocumentsUpload.documentUploads.length > 0 ||
			responseDocumentsUpload.notUploadedDocuments.length > 0 ||
			responseDocumentsUpload.documentUploads.length > 0 ||
			hasDocumentsWithError,
		[commentDocumentsUpload, responseDocumentsUpload, hasDocumentsWithError]
	);

	const isRespondingToApplicant = useMemo(() => {
		return (
			formik.values.action?.value &&
			(isResponseAction(formik.values.action?.value) || formik.values.action?.value === ApplicationEventType.SOLICITACAO_APROVADA)
		);
	}, [formik.values.action?.value]);

	const disableMultiFileInput = !formik.values.action || fileCountCommentDocuments >= baseConfig.maxSubmissionApplicationFiles;
	const disableReplyMultiFileInput = fileCountResponseDocuments >= baseConfig.maxSubmissionApplicationFiles;

	const mapPlateConsultToOptions = (plate: PlateConsultResponse[]) => {
		return plate.map((item) => ({
			label: `${item.empresa} - ${item.tag}`,
			value: item.tag,
			status: item.situacao
		}));
	};

	const today = useMemo(() => {
		return new Date();
	}, []);

	return (
		<div className="pb-5 overflow-hidden overflow-y-auto max-h-[70vh] flex flex-col">
			<div className="new-record-container grow">
				<form onSubmit={formik.handleSubmit} className="new-record-form">
					<div className="flex gap-6 pb-8">
						<SelectForm
							name="action"
							label="Ação:"
							value={formik.values.action}
							touched={formik.touched.action}
							options={actionOptions}
							onBlur={formik.handleBlur}
							setFieldValue={formik.setFieldValue}
							isClearable={false}
							onChange={resetOnActionChange}
							className="w-[400px] mt-0"
						/>
					</div>
					<div className="pb-2">
						<Label isRequired showOptionalIndicator={false}>
							Comentário interno:
						</Label>
						<TextArea
							label="Comentário interno:"
							placeholder="Digite seu comentário"
							name="comment"
							value={formik.values.comment}
							onChange={formik.handleChange}
							touched={formik.touched.comment}
							onBlur={formik.handleBlur}
							maxLength={500}
							disabled={!formik.values.action}
						/>
					</div>
					<div className="pb-6 flex flex-col gap-4">
						<div className="w-fit">
							<FileInput
								handleDrop={commentDocumentsUpload.handleDrop}
								handleInputChange={commentDocumentsUpload.handleInputChange}
								disabled={disableMultiFileInput}
								multiple
							>
								<IcoUpload size="24" color={disableMultiFileInput ? colors.neutral["low-300"] : undefined} />
								<span className="text-nowrap">Anexar arquivos</span>
							</FileInput>
						</div>
						<MultiFileDisplay
							documentsUploaded={formik.values.commentDocuments}
							documentsInUpload={commentDocumentsUpload.documentUploads}
							documentsNotUploaded={commentDocumentsUpload.notUploadedDocuments}
							setDocumentsUploaded={setCommentDocuments}
							setDocumentsNotUploaded={commentDocumentsUpload.setNotUploadedDocuments}
							showFileCountLimit={commentDocumentsUpload.showFileCountLimit}
							columnsDisplay={5}
						/>
					</div>
					{isRespondingToApplicant && (
						<>
							<div className="pb-2">
								<Label isRequired={false}>Resposta ao interessado:</Label>
								<TextArea
									label="Resposta ao interessado:"
									placeholder="Digite sua mensagem"
									name="response"
									value={formik.values.response}
									onChange={formik.handleChange}
									touched={formik.touched.response}
									onBlur={formik.handleBlur}
									maxLength={500}
								/>
							</div>
							<div className="pb-6 flex flex-col gap-4">
								<div className="w-fit">
									<FileInput
										handleDrop={responseDocumentsUpload.handleDrop}
										handleInputChange={responseDocumentsUpload.handleInputChange}
										disabled={disableReplyMultiFileInput}
										multiple
									>
										<IcoUpload size="24" color={disableReplyMultiFileInput ? colors.neutral["low-300"] : undefined} />
										<span className="text-nowrap">Anexar arquivos</span>
									</FileInput>
								</div>
								<MultiFileDisplay
									documentsUploaded={formik.values.responseDocuments}
									documentsInUpload={responseDocumentsUpload.documentUploads}
									documentsNotUploaded={responseDocumentsUpload.notUploadedDocuments}
									setDocumentsUploaded={setResponseDocuments}
									setDocumentsNotUploaded={responseDocumentsUpload.setNotUploadedDocuments}
									showFileCountLimit={responseDocumentsUpload.showFileCountLimit}
									columnsDisplay={5}
								/>
							</div>
						</>
					)}
					{formik.values.action?.value === ApplicationEventType.SOLICITACAO_APROVADA && (
						<div className="flex flex-col gap-4 pb-4">
							<div className="flex gap-4">
								<SelectForm
									name="exemptionMotive"
									label="Motivo da isenção:"
									value={formik.values.exemptionMotive}
									touched={formik.touched.exemptionMotive}
									options={exemptionMotivesOptions}
									onBlur={formik.handleBlur}
									setFieldValue={formik.setFieldValue}
									isClearable={false}
									className="mt-0 flex-1"
								/>
								<SelectForm
									name="exemptionMotiveType"
									label="Tipo da isenção:"
									value={formik.values.exemptionMotiveType}
									touched={formik.touched.exemptionMotiveType}
									options={exemptionMotiveTypesOptions}
									onBlur={formik.handleBlur}
									setFieldValue={formik.setFieldValue}
									isClearable={false}
									className="mt-0 flex-1"
								/>
								<Input
									label="Patrimônio:"
									placeholder="Digite"
									name="propertyCode"
									value={formik.values.propertyCode}
									touched={formik.touched.propertyCode}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									maxLength={251}
									className="flex-1"
									required={false}
								/>
							</div>
							<div className="flex gap-4">
								<div className="flex gap-4 flex-1">
									<Datepicker
										label="Início"
										selectedDate={formik.values.exemptionInitDatetime}
										onChange={(date) => formik.setFieldValue("exemptionInitDatetime", date)}
										minDate={today}
										error={formik.errors?.exemptionInitDatetime}
										touched={formik.touched?.exemptionInitDatetime}
									/>
									<Datepicker
										label="Fim"
										selectedDate={formik.values.exemptionEndDatetime}
										onChange={(date) => formik.setFieldValue("exemptionEndDatetime", date)}
										minDate={
											formik.values.exemptionInitDatetime ? addDays(formik.values.exemptionInitDatetime, 1) : addDays(today, 1)
										}
										error={formik.errors?.exemptionEndDatetime}
										touched={formik.touched?.exemptionEndDatetime}
									/>
								</div>
								<MultiSelect
									name="tollStations"
									label="Praças de pedágio:"
									value={formik.values.tollStations}
									options={tollStationsOptions}
									isSearchable
									error={formik.errors.tollStations}
									touched={formik.touched.tollStations}
									setFieldValue={formik.setFieldValue}
									getOptionValue={OptionHelper.getValue}
									className="flex-1"
									allowSelectAll
									showCheckbox
									shouldShowSelectedValues={false}
								/>
								<SelectForm
									name="vehicleCategory"
									label="Categoria do veículo:"
									value={formik.values.vehicleCategory}
									touched={formik.touched.vehicleCategory}
									options={vehicleCategoriesOptions}
									onBlur={formik.handleBlur}
									setFieldValue={formik.setFieldValue}
									isClearable={false}
									className="mt-0 flex-1"
								/>
							</div>

							{plateData.length >= 1 && (
								<TagRadioInput
									name="tagNumber"
									label="Tag:"
									layout="vertical"
									value={formik.values.tagNumber}
									onChange={formik.handleChange}
									options={mapPlateConsultToOptions(plateData)}
									touched={formik.touched.tagNumber}
									submittedTagNumber={submittedTagNumber}
									error={formik.errors.tagNumber}
									className="text-sm"
									isRequired={false}
								/>
							)}
						</div>
					)}
				</form>
				<div className="footer-buttons">
					<Button hierarchy="secondary" size="medium" onClick={() => setIsOpenModalCancel(true)} disabled={isLoadingSave}>
						Cancelar
					</Button>
					<Button
						hierarchy="primary"
						size="medium"
						onClick={formik.handleSubmit}
						disabled={!(formik.dirty && formik.isValid) || hasPendingDocuments}
						isLoading={isLoadingSave}
					>
						Confirmar
					</Button>
				</div>
			</div>
			<CancelNewRecordModal
				isOpen={isOpenModalCancel}
				onClose={() => setIsOpenModalCancel(false)}
				onClickConfirm={() => {
					formik.setValues(initialStateNewRecord);
					setIsOpenModalCancel(false);
				}}
			/>
		</div>
	);
}
